import { getMonth, getYear, setMonth } from 'date-fns';
import {
  CAPPED_100_CODE,
  CAPPED_2520000_CODE,
  CAPPED_252000_CODE,
  CAPPED_25200_CODE,
  DATA_UNAVAILABLE_TEXT,
  UNAVAILABLE_AMOUNT_CODE,
  UNKNOWN_AMOUNT_CODE,
} from '../../constants/credit-report-constants';
import type { CreditReportType, LocalDate, TradeLine } from '@zg-rentals/ts-capps-client';
import { getCurrencyDisplayFromDollars, getTitleCase } from '@zg-rentals/formatters';
import { parseDate } from '@zg-rentals/formatters';

export const getFullNameForCreditReport = (creditReport: CreditReportType) => {
  const {
    First: firstName,
    Middle: middleName,
    Surname: lastName,
    // Spanish surname, hyphenated name, etc.
    SecondSurname: secondSurname,
    // Junior, III, etc.
    Gen: gen,
  } = creditReport.fullCreditProfile?.ConsumerIdentity?.[0]?.Name || {};

  return firstName && lastName
    ? [
        getTitleCase(firstName),
        getTitleCase(middleName || ''),
        getTitleCase(lastName),
        getTitleCase(secondSurname || ''),
        getTitleCase(gen || ''),
      ]
        .filter((name) => name)
        .join(' ')
    : 'name unavailable';
};

export const getExperianCurrencyDisplay = (amount?: string) => {
  if (amount == null) {
    return '';
  }
  // If it's a crazy Experian code, return the below strings.
  switch (amount) {
    case UNKNOWN_AMOUNT_CODE:
      return 'Unknown amount';
    case UNAVAILABLE_AMOUNT_CODE:
      return 'Unavailable';
    case CAPPED_100_CODE:
      return 'Less than $100';
    case CAPPED_25200_CODE:
      return 'Greater than $25,200';
    case CAPPED_252000_CODE:
      return 'Greater than $252,000';
    case CAPPED_2520000_CODE:
      return 'Greater than $2,520,000';
    default:
      return getCurrencyDisplayFromDollars({ amount, hideCents: true });
  }
};

export const getTotalPayments = (paymentString: string) => {
  if (!paymentString) {
    return 0;
  }
  const regExPattern = '[C|0-9]';
  const payments = (paymentString.match(new RegExp(regExPattern, 'g')) || []).length;
  return payments;
};

export const getOnTimePayments = (paymentString: string) => {
  if (!paymentString) {
    return 0;
  }
  const payments = paymentString.split('C').length - 1 + (paymentString.split('0').length - 1);
  return payments;
};

export const getLatePayments = (paymentString: string) => {
  if (!paymentString) {
    return 0;
  }
  const regExPattern = '[1-9]';
  const payments = (paymentString.match(new RegExp(regExPattern, 'g')) || []).length;
  return payments;
};

export const getLastLatePayment = (paymentString: string, balanceDate: string | LocalDate) => {
  const latePayments = '[1-9]';
  const regEx = new RegExp(latePayments, 'g');
  const latePayment = regEx.exec(paymentString);
  if (latePayment !== null && balanceDate !== null) {
    const paymentIndex = latePayment.index;
    const balanceDateObject = parseDate(balanceDate);
    // Use payment index and BalanceDate to determine the Month and Year of the last late payment
    // by subtracting BalanceDate - (index)
    const lastPaymentDateObject = new Date(setMonth(balanceDateObject, getMonth(balanceDateObject) - paymentIndex));
    // adding 1 because month is 0 indexed
    const month = getMonth(lastPaymentDateObject) + 1;
    const year = getYear(lastPaymentDateObject);

    return `${month}/${year}`;
  }
  return DATA_UNAVAILABLE_TEXT;
};

export const isCollection = (trade: TradeLine) => {
  // KOB = Kind Of Business
  const kobValuesCollection = ['YA', 'YB', 'YC', 'YD', 'YF', 'YL', 'YZ', 'YR', 'ZB', 'ZC', 'ZY'],
    accountTypesCollection = ['0C', '48', '4F'];
  let isCollectionBool = false;
  if (trade.EnhancedPaymentData) {
    if (trade.EnhancedPaymentData.PaymentStatus) {
      if (
        (!trade.EnhancedPaymentData.AccountCondition ||
          Object.keys(trade.EnhancedPaymentData.AccountCondition).length === 0 ||
          trade.EnhancedPaymentData.AccountCondition.code === null) &&
        trade.EnhancedPaymentData.PaymentStatus.code === '93'
      ) {
        isCollectionBool = true;
      }
    }

    if (trade.EnhancedPaymentData.AccountCondition && trade.EnhancedPaymentData.AccountCondition.code) {
      if (trade.EnhancedPaymentData.AccountCondition.code === '93') {
        isCollectionBool = true;
      }
    }
  }

  if (trade.KOB?.code && kobValuesCollection.includes(trade.KOB.code)) {
    isCollectionBool = true;
  }
  if (trade.AccountType?.code && accountTypesCollection.includes(trade.AccountType.code)) {
    isCollectionBool = true;
  }
  return isCollectionBool;
};

export const isOpenAccount = (trade: TradeLine) => {
  const specialCommentCodes = [
      '28',
      '30',
      '31',
      '40',
      '46',
      '50',
      '51',
      '69',
      '09',
      '18',
      '19',
      '25',
      '26',
      '44',
      '45',
      '52',
      '55',
      '58',
      '59',
      '60',
      '61',
      '62',
      '63',
      '64',
      '65',
      '66',
      '67',
      '68',
      '71',
      '72',
      '73',
      '76',
      '83',
      '84',
      '91',
    ],
    paymentStatuses = [
      '07',
      '08',
      '09',
      '11',
      '14',
      '15',
      '16',
      '17',
      '22',
      '23',
      '24',
      '25',
      '26',
      '27',
      '28',
      '29',
      '31',
      '32',
      '33',
      '34',
      '35',
      '36',
      '37',
      '38',
      '39',
      '40',
      '41',
      '42',
      '43',
      '45',
      '71',
      '72',
      '73',
      '74',
      '75',
      '76',
      '77',
      '78',
      '79',
      '80',
      '81',
      '82',
      '83',
      '84',
      '86',
    ],
    accountConditions = ['A1', 'A4', '85', ''];
  let includesSpecialCommentCode = false,
    includesPaymentCode = false,
    includesSpecialCommentCodeAndKOB = false,
    includesSpecialCommentCodeAndAccountTypeCode = false,
    includesAccountConditionCode = false;

  if (trade.SpecialComment) {
    if (trade.SpecialComment.code && specialCommentCodes.includes(trade.SpecialComment.code)) {
      includesSpecialCommentCode = true;
    }
    if (trade.AccountType) {
      if (trade.SpecialComment.code === '29' && trade.AccountType.code === '12') {
        includesSpecialCommentCodeAndAccountTypeCode = true;
      }
    }
    if (trade.KOB && trade.KOB.code) {
      if (trade.SpecialComment.code === '29' && trade.KOB.code[0] === 'E') {
        includesSpecialCommentCodeAndKOB = true;
      }
    }
  }

  if (
    trade.EnhancedPaymentData?.AccountCondition?.code &&
    accountConditions.includes(trade.EnhancedPaymentData.AccountCondition.code)
  ) {
    includesAccountConditionCode = true;
  }
  if (
    trade.EnhancedPaymentData?.PaymentStatus?.code &&
    paymentStatuses.includes(trade.EnhancedPaymentData.PaymentStatus.code)
  ) {
    includesPaymentCode = true;
  }

  return (
    !includesSpecialCommentCode &&
    !includesSpecialCommentCodeAndKOB &&
    !includesSpecialCommentCodeAndAccountTypeCode &&
    includesAccountConditionCode &&
    includesPaymentCode
  );
};

export const getNumberFromString = (string?: string | null) => {
  if (!string) {
    return 0;
  }
  if (typeof string === 'string') {
    string = string.replace(/[^0-9.]/g, '');
  }

  return !isNaN(parseFloat(string)) ? parseFloat(string) : 0;
};
